import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { IPeriodsTableRows } from 'components/BusinessToolForm/constants/periods';
import { CALENDAR_PLAN_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/CalendarPlanForm/utils/form';
import { FUNDING_DIRECTIONS_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/FundingDirectionsForm/utils/form';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/LoanRepaymentScheduleForm/utils/form';
import {
  calendarPlanPrefix,
  fundingDirectionsPrefix,
  loanRepaymentSchedulePrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/utils/form';
import { NET_INCOME_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetIncome/utils/form';
import { netIncomePrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/utils/form';
import { FINANCING_ACTIVITIES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/FinancingActivities/utils/form';
import { INVESTING_ACTIVITIES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/InvestingActivities/utils/form';
import { OPERATING_ACTIVITIES_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/DirectForm/DirectFormComponents/Sections/OperatingActivities/utils/form';
import { OTHER_ITEMS_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/InDirectForm/InDirectFormComponents/Sections/OtherItemsForm/utils/form';
import {
  financingActivitiesPrefix,
  investingActivitiesPrefix,
  operatingActivitiesPrefix,
  otherItemsPrefix,
} from 'components/BusinessToolForm/Sections/FinancialPlan/ProjectedCashFlow/utils/form';
import { prefix, REVENUE_PLAN_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/RevenuePlan/utils/form';
import {
  convertToOpposite,
  PERIODS_OPERATION_TYPES,
  recalculateOnUnitUpdate,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

export const useUpdateFinancialPlanTables = () => {
  const { setValue, getValues } = useFormContext();

  // revenue
  const otherIncomeField = concatWithDot(prefix, REVENUE_PLAN_NAMES.PLANNED_PRODUCTS_VOLUME_OTHER_INCOME);

  // profit and loos
  const financialIncomeField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.FINANCIAL_INCOME);
  const nonOperatingIncomeField = concatWithDot(netIncomePrefix, NET_INCOME_NAMES.NON_OPERATING_INCOME);
  const interestReceivedField = concatWithDot(operatingActivitiesPrefix, OPERATING_ACTIVITIES_NAMES.INTEREST_RECEIVED);
  const interestReceivedIndirectField = concatWithDot(otherItemsPrefix, OTHER_ITEMS_NAMES.INTEREST_RECEIVED);

  // cash flow
  const cashFlowElementsPlaningTableField = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TABLE);
  const cashFlowElementsPlaningTotalField = concatWithDot(calendarPlanPrefix, CALENDAR_PLAN_NAMES.TOTAL);
  const loanPaymentTableField = concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.TABLE);
  const repaymentOfLoanField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.REPAYMENT_DEBT);
  const fundingDirectionsTableField = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.TABLE);
  const fundingDirectionsTotalField = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.TOTAL);
  const rAndDSubRows = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.R_AND_D_SUB_ROWS);
  const marketingAndSalesSubRows = concatWithDot(
    fundingDirectionsPrefix,
    FUNDING_DIRECTIONS_NAMES.MARKETING_AND_SALES_SUB_ROWS,
  );
  const workingCapitalSubRow = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.WORKING_CAPITAL_SUB_ROW);
  const constructionSubRow = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.CONSTRUCTION_SUB_ROW);
  const equipmentPurchaseSubRow = concatWithDot(
    fundingDirectionsPrefix,
    FUNDING_DIRECTIONS_NAMES.EQUIPMENT_PURCHASE_SUB_ROW,
  );
  const otherSubRow = concatWithDot(fundingDirectionsPrefix, FUNDING_DIRECTIONS_NAMES.OTHER_SUB_ROW);
  const issuanceDebtField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_DEBT);
  const issuanceEquityField = concatWithDot(
    financingActivitiesPrefix,
    FINANCING_ACTIVITIES_NAMES.PROCEEDS_ISSUANCE_EQUITY,
  );
  const purchasePropertyField = concatWithDot(
    investingActivitiesPrefix,
    INVESTING_ACTIVITIES_NAMES.PURCHASE_PROPERTY_PLANT_EQUIPMENT,
  );

  // projected balance sheet
  const investingActivitiesField = concatWithDot(investingActivitiesPrefix, INVESTING_ACTIVITIES_NAMES.TABLE);
  const financialActivitiesField = concatWithDot(financingActivitiesPrefix, FINANCING_ACTIVITIES_NAMES.TABLE);

  const financialPlanTablesUpdateHandler = useCallback((multiplier: number) => {
    
    // revenue
    const otherIncome = getValues(otherIncomeField);

    // profit and loos
    const financialIncome = getValues(financialIncomeField);
    const nonOperatingIncome = getValues(nonOperatingIncomeField);

    // cash flow
    const cashFlowElementsPlaningTable = getValues(cashFlowElementsPlaningTableField);
    const loanPaymentTable = getValues(loanPaymentTableField);
    const fundingDirectionsTable = getValues(fundingDirectionsTableField);
    const rAndDSubRowsTable = getValues(rAndDSubRows);
    const marketingAndSalesSubRowsTable = getValues(marketingAndSalesSubRows);
    const workingCapitalSubRowTable = getValues(workingCapitalSubRow);
    const constructionSubRowTable = getValues(constructionSubRow);
    const equipmentPurchaseSubRowTable = getValues(equipmentPurchaseSubRow);
    const otherSubRowTable = getValues(otherSubRow);

    // projected cash flow
    const investingActivities = getValues(investingActivitiesField);
    const financialActivities = getValues(financialActivitiesField);

    // revenue
    const updatedOtherIncome = recalculateOnUnitUpdate(otherIncome[0], multiplier);

    // profit and loos
    const updatedFinancialIncome = recalculateOnUnitUpdate(financialIncome[0], multiplier);
    const updatedNonOperatingIncome = recalculateOnUnitUpdate(nonOperatingIncome[0], multiplier);

    // cash flow
    const updatedCashFlowElementsPlaningTable = cashFlowElementsPlaningTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedCashFlowElementsPlaningTotal = summarizeSubtractPeriodValues(
      updatedCashFlowElementsPlaningTable,
      PERIODS_OPERATION_TYPES.ADD,
    );
    const ownFunds = updatedCashFlowElementsPlaningTable[0];
    const borrowedFunds = updatedCashFlowElementsPlaningTable[1];
    const investmentFunds = updatedCashFlowElementsPlaningTable[2];
    const issuanceEquity = summarizeSubtractPeriodValues([ownFunds, investmentFunds], PERIODS_OPERATION_TYPES.ADD);
    const updatedLoanPayment = recalculateOnUnitUpdate(loanPaymentTable[0], multiplier);
    const updatedLoanPaymentTable = [updatedLoanPayment, loanPaymentTable[1]];
    const updatedFundingDirectionsTable = fundingDirectionsTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedFundingDirectionsTotal = summarizeSubtractPeriodValues(
      updatedFundingDirectionsTable,
      PERIODS_OPERATION_TYPES.ADD,
    );
    const constructionEquipment = updatedFundingDirectionsTable.slice(3, 5);
    const purchaseProperty = convertToOpposite(summarizeSubtractPeriodValues(
      [constructionEquipment[0], constructionEquipment[1]],
      PERIODS_OPERATION_TYPES.ADD,
    ));
    const updatedRAndDSubRowsTable = rAndDSubRowsTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedMarketingAndSalesSubRowsTable = marketingAndSalesSubRowsTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedWorkingCapitalSubRowTable = workingCapitalSubRowTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedConstructionSubRowTable = constructionSubRowTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedEquipmentPurchaseSubRowTable = equipmentPurchaseSubRowTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedOtherSubRowTable = otherSubRowTable.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );

    // projected balance sheet
    const updatedInvestingActivities = investingActivities.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );
    const updatedFinancialActivities = financialActivities.map((row: IPeriodsTableRows) =>
      recalculateOnUnitUpdate(row, multiplier),
    );

    // revenue
    setValue(otherIncomeField, [updatedOtherIncome]);

    // profit and loos
    setValue(financialIncomeField, [updatedFinancialIncome]);
    setValue(interestReceivedField, updatedFinancialIncome);
    setValue(interestReceivedIndirectField, updatedFinancialIncome);
    setValue(nonOperatingIncomeField, [updatedNonOperatingIncome]);

    // cash flow
    setValue(cashFlowElementsPlaningTableField, updatedCashFlowElementsPlaningTable);
    setValue(cashFlowElementsPlaningTotalField, updatedCashFlowElementsPlaningTotal);
    setValue(loanPaymentTableField, updatedLoanPaymentTable);
    setValue(repaymentOfLoanField, convertToOpposite(updatedLoanPayment));
    setValue(fundingDirectionsTableField, updatedFundingDirectionsTable);
    setValue(fundingDirectionsTotalField, updatedFundingDirectionsTotal);
    setValue(rAndDSubRows, updatedRAndDSubRowsTable);
    setValue(marketingAndSalesSubRows, updatedMarketingAndSalesSubRowsTable);
    setValue(workingCapitalSubRow, updatedWorkingCapitalSubRowTable);
    setValue(constructionSubRow, updatedConstructionSubRowTable);
    setValue(equipmentPurchaseSubRow, updatedEquipmentPurchaseSubRowTable);
    setValue(otherSubRow, updatedOtherSubRowTable);
    setValue(issuanceDebtField, borrowedFunds);
    setValue(issuanceEquityField, issuanceEquity);
    setValue(purchasePropertyField, purchaseProperty);

    // projected cash flow
    setValue(investingActivitiesField, updatedInvestingActivities);
    setValue(financialActivitiesField, updatedFinancialActivities);
  }, []);

  return financialPlanTablesUpdateHandler;
};
